import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  buttonVariants,
  cn,
} from '@kalos/ui';
import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';

import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { type MenuItem, type NavOption, type SubMenu } from '../main';

type Props = {
  item: MenuItem;
};

const KalosMenuItem = ({ item }: Props) => {
  if ('items' in item) {
    return <DropdownSubMenu item={item} />;
  }

  return <NavOptionComponent item={item} />;
};

const itemValue = 'itemValue';
const DropdownSubMenu: React.FC<{ item: SubMenu }> = ({ item }) => {
  const [sideMenuState, setSideMenuState] = useLocalStorage(
    'sideMenuDropdownState',
    useMemo(
      () => ({
        home: true,
        profile: true,
        customers: false,
        dispatch: false,
        reporting: false,
        team: false,
        finance: false,
        admin: false,
        tools: false,
      }),
      [],
    ),
  );

  const selectedValues = useMemo(
    () => (sideMenuState[item.localStorageKey] ? [itemValue] : []),
    [item.localStorageKey, sideMenuState],
  );

  return (
    <Accordion
      className="border-foreground/20 border-b"
      type="multiple"
      onValueChange={(newValues) => {
        setSideMenuState((prev) => ({
          ...prev,
          [item.localStorageKey]: newValues.includes(itemValue),
        }));
      }}
      value={selectedValues}
    >
      <AccordionItem value={itemValue}>
        <AccordionTrigger className="pb-0.5 pt-0 text-sm">{item.heading}</AccordionTrigger>
        <AccordionContent className="min-w-0 py-1">
          {selectedValues.includes(itemValue) && (
            <ul className="min-w-0 space-y-0.5">
              {item.items.map((subItem, index) => (
                <NavOptionComponent key={index} item={subItem} />
              ))}
            </ul>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export type NavOptionProps = {
  item: NavOption;
};

const menuItemClassName = cn(
  buttonVariants({ variant: 'ghost' }),
  'mx-0.5 flex h-auto min-h-8 w-full max-w-full min-w-0 items-center gap-1.5 whitespace-normal px-3 py-2 text-sm text-left',
);

/** Link, anchor, or button row without wrapping `<li>` — for composing search result rows. */
export const NavOptionRow: React.FC<NavOptionProps & { isSubModule?: boolean }> = ({
  item,
  isSubModule,
}) => {
  const children = (
    <>
      <div className={cn('flex shrink-0 items-center', isSubModule && 'opacity-60')}>
        {item.icon}
      </div>
      <span
        className={cn(
          'min-w-0 max-w-full flex-1 whitespace-normal break-words text-left text-sm',
          isSubModule && 'text-muted-foreground',
        )}
        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
      >
        {item.title}
      </span>
    </>
  );

  const itemTarget = 'target' in item ? item.target : undefined;
  const itemHref = 'href' in item ? item.href : undefined;

  const linkElement =
    'route' in item ? (
      <Link
        {...item.route}
        onClick={item.onClick}
        className={menuItemClassName}
        activeProps={{
          className: cn(
            buttonVariants({ variant: 'default' }),
            'mx-0.5 flex h-auto min-h-8 w-full max-w-full min-w-0 items-center gap-1.5 whitespace-normal px-3 py-2 text-sm text-left hover:text-background',
          ),
        }}
        target={itemTarget}
      >
        {children}
      </Link>
    ) : (
      <a href={itemHref} target={itemTarget} className={menuItemClassName}>
        {children}
      </a>
    );

  if ('button' in item) {
    return (
      <Button className={cn(menuItemClassName, 'flex-1')} onClick={item.onClick} variant="ghost">
        {children}
      </Button>
    );
  }

  return linkElement;
};

export type NavOptionComponentProps = NavOptionProps & {
  sectionLabel?: string;
  isSubModule?: boolean;
};

export const NavOptionComponent: React.FC<NavOptionComponentProps> = ({
  item,
  sectionLabel,
  isSubModule,
}) => {
  if (sectionLabel) {
    return (
      <li className="flex min-w-0 list-none flex-col gap-0.5">
        <span className="text-muted-foreground px-3 text-xs">{sectionLabel}</span>
        <div className="flex w-full min-w-0">
          <NavOptionRow item={item} isSubModule={isSubModule} />
        </div>
      </li>
    );
  }

  return (
    <li className="flex min-w-0">
      <NavOptionRow item={item} isSubModule={isSubModule} />
    </li>
  );
};

export default KalosMenuItem;
